import {BaseController} from "@nolimitcity/slot-game/bin/core/base/BaseController";
import {BookOfShadowsInactiveRowView} from "./BookOfShadowsInactiveRowView";
import {BookOfShadowsParsedInitData} from "../server/data/BookOfShadowsParsedInitData";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {SpinStart} from "../gameround/spin/SpinStart";
import {IBookOfShadowsReplayNextPlayerInteraction} from "../transitions/freeSpinIntro/BookOfShadowsFreeSpinIntroView";
import {BookOfShadows} from "../BookOfShadows";
import {BookOfShadowsParsedGameData} from "../server/data/BookOfShadowsParsedGameData";
import {BalanceEvent} from "@nolimitcity/slot-game/bin/core/balance/event/BalanceEvent";
import {APIBetType} from "@nolimitcity/slot-launcher/bin/interfaces/APIBetType";
import {GameSetting} from "@nolimitcity/slot-game/bin/core/setting/GameSetting";
import {BookOfShadowsGameConfig} from "../BookOfShadowsGameConfig";

export interface IFeatureBetEventData {
    type: APIBetType;
    bet: string;
    featureName: string;
}

export class BookOfShadowsInactiveRowController extends BaseController {
    private _view: BookOfShadowsInactiveRowView;
    private _selectedBaseRows: number = 3;

    /**
     * @constructs
     */
    constructor() {
        super();
        this._view = new BookOfShadowsInactiveRowView();

        this.addEventListeners();
    }

    public updateRowsOverlay(rowsChanged:boolean){
        this._view.updateOverlay(BookOfShadowsGameConfig.instance.ACTIVE_ROWS, rowsChanged);
    }

    /**
     * @description When entered FS.
     */
    public enteredFS(): void {
        this._view.enteredFS();
    }

    /**
     * @description When exited FS.
     */
    public updateVisibleRows(val?: number): void {
        this._view.updateNumberOfVisibleRows(val ? val : this._selectedBaseRows);
    }

    /**
     * @description To enable the row selector when the game goes to idle mode.
     */
    public onIdle(): void {
        this._view.showArrowButton(true);
    }

    /**
     * @description When symbol shuffle completed on FS intro.
     */
    public symbolShuffleFSCompleted(position: number[]): void {
        this._view.performRowPickForFSIntro(position);
    }

    /**
     * @description When amount item clicked on FS intro.
     * @param selectedIndex
     */
    public handleReplay(selectedIndex: string): void {
        this._view.handleReplay(selectedIndex);
    }

    /**
     * @description To perform boosted bet in view.
     */
    public setBoostedBet(): void {
        this._view.setBoostedBet();
    }

    /**
     * @description To register all the event listeners.
     */
    private addEventListeners(): void {
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event: GameEvent) => this.onInitDataParsed(event.params[0]));
        EventHandler.addEventListener(this, ServerEvent.GAME_DATA_PARSED, (event: GameEvent) => this.onGameDataParsed(event.params[0]));
        EventHandler.addEventListener(this, SpinStart.SPIN_STARTED_EVENT, () => this._view.enableInActiveRowOverlay(false));
        EventHandler.addEventListener(this, BalanceEvent.BET, (event: GameEvent) => this.onBet(event.params[0]));
    }

    /**
     * @description Method called when init data is parsed.
     * @param data
     */
    private onInitDataParsed(data: BookOfShadowsParsedInitData): void {
        let rows: number = (data.nextReelSetName === "BASE_REELSET" ? data.selectedRows : data.selectedRowsFS),
            replayRows: string | undefined,
            featureName: string | undefined;

        if (GameSetting.replayMode) {
            replayRows = (data.replayNextPlayerInteraction as IBookOfShadowsReplayNextPlayerInteraction).baseRows;
            if (replayRows) {
                rows = this._selectedBaseRows = parseInt(replayRows);

                BookOfShadowsGameConfig.instance.ACTIVE_ROWS = rows;
                BookOfShadows.setBoostedBet();
            } else {
                featureName = (data.replayNextPlayerInteraction as IBookOfShadowsReplayNextPlayerInteraction).featureName;
                if (featureName) {
                    if (featureName.indexOf("3") !== -1) {
                        rows = 3;
                    } else if (featureName.indexOf("4") !== -1) {
                        rows = 4;
                    } else if (featureName.indexOf("5") !== -1) {
                        rows = 5;
                    }
                }
            }
        } else {
            this._selectedBaseRows = data.selectedRows;

            // #179
            if (data.isRestoreState && !data.wasFeatureBuy) {
                // this is only used for setting boosted bet in FS restore only, it will be reset once FS is over.
                if (data.nextReelSetName === "FREESPIN_REELSET" && data.lockedReelBet > 0) {
                    BookOfShadowsGameConfig.instance.LOCKED_REEL = ["1", "0", "0", "0", "0"]; // fake data.
                }

                BookOfShadowsGameConfig.instance.ACTIVE_ROWS = data.selectedRows;
                BookOfShadows.setBoostedBet();
            }
        }

        this._view.onGameInit(rows, data.isRestoreState, data.wasFeatureBuy);

        EventHandler.removeEventListener(this, ServerEvent.INIT_DATA_PARSED);
    }

    /**
     * @description Method called when game data is parsed.
     * @param data
     */
    private onGameDataParsed(data: BookOfShadowsParsedGameData): void {
        this._selectedBaseRows = data.selectedRows;
    }

    /**
     * @description When we hit a bet request, only interested in FREESPIN feature buy.
     * @param bet
     */
    private onBet(bet: IFeatureBetEventData): void {
        const featureName: string | undefined = bet.featureName

        if (bet.type === APIBetType.FEATURE_BET && featureName && featureName.indexOf("FREESPIN") > -1) {
            let val: number = 3;
            if (featureName.indexOf("3") !== -1) {
                val = 3;
            } else if (featureName.indexOf("4") !== -1) {
                val = 4;
            } else if (featureName.indexOf("5") !== -1) {
                val = 5;
            }

            // #172
            BookOfShadowsGameConfig.instance.LOCKED_REEL = ["0", "0", "0", "0", "0"];
            const copyActiveRows: number = BookOfShadowsGameConfig.instance.ACTIVE_ROWS;
            BookOfShadowsGameConfig.instance.ACTIVE_ROWS = 3;
            BookOfShadows.setBoostedBet();
            BookOfShadowsGameConfig.instance.ACTIVE_ROWS = copyActiveRows;

            this._view.updateNumberOfVisibleRows(val);
        }
        else if (bet.type === APIBetType.FEATURE_BET && featureName && featureName.indexOf("BOOSTED_BET") > -1){
            let val = 3;
            if (BookOfShadows.api.betHandler.betBoost != undefined) {
                if (BookOfShadows.api.betHandler.betBoost.featureName == "BOOSTED_BET_4_ROWS"){
                    val =  4;
                }
                if (BookOfShadows.api.betHandler.betBoost.featureName == "BOOSTED_BET_5_ROWS"){
                    val =  5;
                }
            }
            this._view.updateNumberOfVisibleRows(val);
        }


        if ((<any>bet).isFakeBet == true) {
            //This is the first "fake bet" that starts bonus in game when playing AS.
            let val = 3;
            if (BookOfShadows.api.betHandler.betBoost != undefined) {
                if (BookOfShadows.api.betHandler.betBoost.featureName == "BOOSTED_BET_4_ROWS"){
                    val =  4;
                }
                if (BookOfShadows.api.betHandler.betBoost.featureName == "BOOSTED_BET_5_ROWS"){
                    val =  5;
                }
            }
            this._view.updateNumberOfVisibleRows(val, true);
        }

        this._view.showArrowButton(false);
    }
}